/**
 * Admin Client Detail Page
 * Shows client details with tabbed interface (General tab in this slice).
 */
import { useCallback, useEffect, useState } from "react";
import { ArrowLeft, ChevronRight, LinkExternal01 } from "@untitledui/icons";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { Tab, TabList, TabPanel, Tabs } from "@/components/application/tabs/tabs";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { useAdminApi } from "@/services/api/admin-api";
import type { ClientAdminDetailDto } from "@/types/api/admin";
import { cx } from "@/utils/cx";
import { AdminShell } from "./AdminShell";
import { AuditEventsTab } from "./components/AuditEventsTab";
import { BridgeTab } from "./components/BridgeTab";
import { EmailSourcesTab } from "./components/EmailSourcesTab";
import { ExceptionsTab } from "./components/ExceptionsTab";
import { FieldsTab } from "./components/FieldsTab";
import { GeneralTab, UsageSection } from "./components/GeneralTab";
import { JobsTab } from "./components/JobsTab";
import { MonitoringTab } from "./components/MonitoringTab";
import { OrganizationsTab } from "./components/OrganizationsTab";
import { PlansTab } from "./components/PlansTab";
import { PromptsTab } from "./components/PromptsTab";
import { UsersTab } from "./components/UsersTab";
import { WorkflowsTab } from "./components/WorkflowsTab";

// =============================================================================
// TAB DEFINITIONS
// =============================================================================

const TABS = [
    { id: "general", label: "General" },
    { id: "organizations", label: "Organizations" },
    { id: "plans", label: "Plans" },
    { id: "fields", label: "Fields" },
    { id: "users", label: "Users" },
    { id: "workflows", label: "Workflows" },
    { id: "prompts", label: "Prompts" },
    { id: "email-sources", label: "Email Sources" },
    { id: "bridge", label: "Bridge" },
    { id: "exceptions", label: "Exceptions" },
    { id: "jobs", label: "Jobs" },
    { id: "audit-events", label: "Audit Events" },
    { id: "monitoring", label: "Monitoring" },
];

// =============================================================================
// ADMIN CLIENT PAGE
// =============================================================================

export default function AdminClientPage() {
    const { clientId } = useParams<{ clientId: string }>();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const adminApi = useAdminApi();

    const requestedTab = searchParams.get("tab") ?? "general";
    const initialTab = TABS.find((t) => t.id === requestedTab)?.id ?? "general";

    const [client, setClient] = useState<ClientAdminDetailDto | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [clientAdminMailboxEnabled, setClientAdminMailboxEnabled] = useState(false);

    const fetchClient = useCallback(async () => {
        if (!clientId) return;
        setIsLoading(true);
        setError(null);
        try {
            const [data, entitlements] = await Promise.all([adminApi.getClient(Number(clientId)), adminApi.listClientPlanEntitlements(Number(clientId))]);
            setClient(data);
            setClientAdminMailboxEnabled(entitlements.some((e) => e.entitlementCode === "FEATURE_CLIENT_ADMIN_MAILBOX" && e.isEnabled));
        } catch (err) {
            setError(err instanceof Error ? err.message : "Failed to load client.");
        } finally {
            setIsLoading(false);
        }
    }, [adminApi, clientId]);

    useEffect(() => {
        fetchClient();
    }, [fetchClient]);

    if (isLoading) {
        return (
            <AdminShell>
                <div className="flex h-full items-center justify-center">
                    <LoadingIndicator size="lg" label="Loading client..." />
                </div>
            </AdminShell>
        );
    }

    if (error || !client) {
        return (
            <AdminShell>
                <div className="flex h-full flex-col items-center justify-center gap-4">
                    <p className="text-sm text-error-primary">{error || "Client not found."}</p>
                    <Button color="secondary" size="sm" onClick={() => navigate("/admin")}>
                        Back to Admin
                    </Button>
                </div>
            </AdminShell>
        );
    }

    return (
        <AdminShell>
            <div className="mx-auto w-full max-w-[90rem] px-6 py-8">
                {/* Breadcrumb */}
                <nav className="mb-4 flex items-center gap-1.5 text-sm text-tertiary">
                    <button onClick={() => navigate("/admin")} className="cursor-pointer transition-colors hover:text-primary">
                        Admin
                    </button>
                    <ChevronRight className="size-4" />
                    <span>Clients</span>
                    <ChevronRight className="size-4" />
                    <span className="text-primary">{client.name}</span>
                </nav>

                {/* Back button + Header */}
                <div className="mb-6">
                    <Button color="link-gray" size="sm" iconLeading={ArrowLeft} onClick={() => navigate("/admin")}>
                        Back to clients
                    </Button>

                    <div className="mt-3 flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <h1 className="text-display-xs font-semibold text-primary">{client.name}</h1>
                            <Badge color="gray" size="sm" type="pill-color">
                                {client.code}
                            </Badge>
                            <Badge color={client.isActive ? "success" : "gray"} size="sm" type="pill-color">
                                {client.isActive ? "Active" : "Inactive"}
                            </Badge>
                        </div>
                        <div className="flex items-center gap-4">
                            <Button color="secondary" size="sm" iconLeading={LinkExternal01} onClick={() => navigate(`/admin/preview/${client.id}/home`)}>
                                Go to User UI
                            </Button>
                            <UsageSection clientId={client.id} />
                        </div>
                    </div>
                </div>

                {/* Tabs */}
                <Tabs defaultSelectedKey={initialTab}>
                    <TabList items={TABS} type="underline" size="sm">
                        {(item) => <Tab id={item.id}>{item.label}</Tab>}
                    </TabList>

                    <TabPanel id="general" className="pt-6">
                        <GeneralTab client={client} onClientUpdated={fetchClient} />
                    </TabPanel>

                    <TabPanel id="organizations" className="pt-6">
                        <OrganizationsTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="plans" className="pt-6">
                        <PlansTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="fields" className="pt-6">
                        <FieldsTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="users" className="pt-6">
                        <UsersTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="workflows" className="pt-6">
                        <WorkflowsTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="prompts" className="pt-6">
                        <PromptsTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="email-sources" className="pt-6">
                        {clientAdminMailboxEnabled && (
                            <div className="mb-4 rounded-lg border border-secondary bg-secondary_subtle px-4 py-3 text-sm text-secondary">
                                This client manages their own mailbox connections. Changes made here will also be visible to their admin.
                            </div>
                        )}
                        <EmailSourcesTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="bridge" className="pt-6">
                        <BridgeTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="exceptions" className="pt-6">
                        <ExceptionsTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="jobs" className="pt-6">
                        <JobsTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="audit-events" className="pt-6">
                        <AuditEventsTab clientId={client.id} />
                    </TabPanel>

                    <TabPanel id="monitoring" className="pt-6">
                        <MonitoringTab clientId={client.id} />
                    </TabPanel>
                </Tabs>
            </div>
        </AdminShell>
    );
}
